import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/redux';
import api from '../../utils/axiosConfig';
import { showToast } from '../../features/ui/uiSlice';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    EyeIcon,
    ChatBubbleLeftRightIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
    ExclamationTriangleIcon,
    UserIcon,
    CalendarIcon,
    TagIcon,
    XMarkIcon,
    PaperAirplaneIcon,
    AdjustmentsHorizontalIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    SparklesIcon,
    ShieldCheckIcon,
    ChatBubbleLeftIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline';

interface SupportTicket {
    _id: string;
    subject: string;
    description: string;
    category: string;
    priority: string;
    status: string;
    user: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone?: string;
    };
    assignedTo?: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    };
    messages: Array<{
        _id: string;
        sender: {
            _id: string;
            firstName: string;
            lastName: string;
            email: string;
        };
        senderType: string;
        message: string;
        isInternal: boolean;
        createdAt: string;
    }>;
    createdAt: string;
    lastActivity: string;
    resolvedAt?: string;
    closedAt?: string;
}

const CustomerSupport: React.FC = () => {
    const { user } = useAppSelector((state) => state.auth);
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
    const [showTicketDetails, setShowTicketDetails] = useState(false);
    const [filters, setFilters] = useState({
        status: '',
        category: '',
        priority: '',
        search: ''
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pages: 1,
        total: 0
    });

    // Form state
    const [formData, setFormData] = useState({
        subject: '',
        description: '',
        category: 'general',
        priority: 'medium'
    });

    // Message state
    const [newMessage, setNewMessage] = useState('');
    const [isInternalMessage, setIsInternalMessage] = useState(false);

    // Loading states
    const [isCreatingTicket, setIsCreatingTicket] = useState(false);
    const [isAddingMessage, setIsAddingMessage] = useState(false);

    // UI states
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        fetchTickets();
    }, [filters, pagination.current]);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.current.toString(),
                limit: '10',
                ...filters
            });

            const endpoint = user?.role === 'user' ? '/customer-support/my-tickets' : '/customer-support';
            const response = await api.get(`${endpoint}?${params}`);

            setTickets(response.data.data);
            setPagination(response.data.pagination);
        } catch (error: any) {
            showToast({
                message: error.response?.data?.message || 'Failed to fetch tickets',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsCreatingTicket(true);
            await api.post('/customer-support', formData);
            showToast({
                message: 'Support ticket created successfully',
                type: 'success'
            });
            setShowCreateForm(false);
            setFormData({
                subject: '',
                description: '',
                category: 'general',
                priority: 'medium'
            });
            fetchTickets();
        } catch (error: any) {
            showToast({
                message: error.response?.data?.message || 'Failed to create ticket',
                type: 'error'
            });
        } finally {
            setIsCreatingTicket(false);
        }
    };

    const handleAddMessage = async () => {
        if (!selectedTicket || !newMessage.trim()) return;

        try {
            setIsAddingMessage(true);
            await api.post(`/customer-support/${selectedTicket._id}/messages`, {
                message: newMessage,
                isInternal: isInternalMessage
            });

            setNewMessage('');
            setIsInternalMessage(false);
            fetchTicketDetails(selectedTicket._id);
            showToast({
                message: 'Message added successfully',
                type: 'success'
            });
        } catch (error: any) {
            showToast({
                message: error.response?.data?.message || 'Failed to add message',
                type: 'error'
            });
        } finally {
            setIsAddingMessage(false);
        }
    };

    const fetchTicketDetails = async (ticketId: string) => {
        try {
            const response = await api.get(`/customer-support/${ticketId}`);
            setSelectedTicket(response.data.data);
        } catch (error: any) {
            showToast({
                message: error.response?.data?.message || 'Failed to fetch ticket details',
                type: 'error'
            });
        }
    };

    const handleStatusChange = async (ticketId: string, status: string) => {
        try {
            await api.put(`/customer-support/${ticketId}/status`, { status });
            showToast({
                message: 'Ticket status updated successfully',
                type: 'success'
            });
            fetchTickets();
            if (selectedTicket?._id === ticketId) {
                fetchTicketDetails(ticketId);
            }
        } catch (error: any) {
            showToast({
                message: error.response?.data?.message || 'Failed to update status',
                type: 'error'
            });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'open': return 'bg-red-100 text-red-800';
            case 'in_progress': return 'bg-yellow-100 text-yellow-800';
            case 'resolved': return 'bg-green-100 text-green-800';
            case 'closed': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'urgent': return 'bg-red-100 text-red-800';
            case 'high': return 'bg-orange-100 text-orange-800';
            case 'medium': return 'bg-yellow-100 text-yellow-800';
            case 'low': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'open': return <ExclamationTriangleIcon className="h-4 w-4" />;
            case 'in_progress': return <ClockIcon className="h-4 w-4" />;
            case 'resolved': return <CheckCircleIcon className="h-4 w-4" />;
            case 'closed': return <XCircleIcon className="h-4 w-4" />;
            default: return <ClockIcon className="h-4 w-4" />;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center space-x-3 mb-4">
                        <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                            <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
                        </div>
                        <div>
                            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                                Customer Support
                            </h1>
                            <div className="flex items-center space-x-2 mt-1">
                                <SparklesIcon className="h-4 w-4 text-blue-500" />
                                <span className="text-sm text-gray-600 font-medium">
                                    {user?.role === 'user'
                                        ? 'Get help with your issues and track your tickets'
                                        : 'Manage support tickets and provide excellent assistance'
                                    }
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="mb-8">
                    <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
                        {user?.role === 'user' && (
                            <button
                                onClick={() => setShowCreateForm(true)}
                                className="group relative inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-all duration-200"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl blur opacity-30 group-hover:opacity-50 transition-opacity"></div>
                                <PlusIcon className="h-5 w-5 mr-2 relative z-10" />
                                <span className="relative z-10">Create Support Ticket</span>
                            </button>
                        )}

                        {/* Search and Filters */}
                        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                            {/* Search */}
                            <div className="relative">
                                <MagnifyingGlassIcon className="h-5 w-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search tickets..."
                                    value={filters.search}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                    className="pl-12 pr-4 py-3 w-full sm:w-64 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm shadow-sm transition-all duration-200"
                                />
                            </div>

                            {/* Filter Toggle */}
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="inline-flex items-center px-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm shadow-sm hover:bg-white hover:shadow-md transition-all duration-200"
                            >
                                <AdjustmentsHorizontalIcon className="h-5 w-5 mr-2 text-gray-600" />
                                <span className="text-sm font-medium text-gray-700">Filters</span>
                                {showFilters ? (
                                    <ChevronUpIcon className="h-4 w-4 ml-2 text-gray-500" />
                                ) : (
                                    <ChevronDownIcon className="h-4 w-4 ml-2 text-gray-500" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Advanced Filters */}
                    {showFilters && (
                        <div className="mt-4 p-6 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                                    >
                                        <option value="">All Status</option>
                                        <option value="open">Open</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="resolved">Resolved</option>
                                        <option value="closed">Closed</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                                    <select
                                        value={filters.category}
                                        onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                                    >
                                        <option value="">All Categories</option>
                                        <option value="technical">Technical</option>
                                        <option value="billing">Billing</option>
                                        <option value="account">Account</option>
                                        <option value="task">Task</option>
                                        <option value="general">General</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                                    <select
                                        value={filters.priority}
                                        onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                                    >
                                        <option value="">All Priorities</option>
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tickets List */}
                <div className="bg-white/80 backdrop-blur-sm shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="px-8 py-6 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">
                                        {user?.role === 'user' ? 'Your Support Tickets' : 'All Support Tickets'}
                                    </h2>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {pagination.total} ticket{pagination.total !== 1 ? 's' : ''} found
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-2xl font-bold text-gray-900">{pagination.total}</div>
                                <div className="text-sm text-gray-500">Total Tickets</div>
                            </div>
                        </div>
                    </div>

                    <div className="divide-y divide-gray-100">
                        {tickets.length === 0 ? (
                            <div className="px-8 py-16 text-center">
                                <div className="mx-auto w-24 h-24 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mb-6">
                                    <ChatBubbleLeftRightIcon className="h-12 w-12 text-blue-500" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">No tickets found</h3>
                                <p className="text-sm text-gray-600 max-w-md mx-auto">
                                    {user?.role === 'user'
                                        ? 'You haven\'t created any support tickets yet. Create your first ticket to get help with any issues.'
                                        : 'No support tickets match your current filters. Try adjusting your search criteria.'
                                    }
                                </p>
                                {user?.role === 'user' && (
                                    <button
                                        onClick={() => setShowCreateForm(true)}
                                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        Create Your First Ticket
                                    </button>
                                )}
                            </div>
                        ) : (
                            tickets.map((ticket) => (
                                <div
                                    key={ticket._id}
                                    className="group p-6 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 cursor-pointer transition-all duration-200 border-l-4 border-transparent hover:border-blue-500"
                                    onClick={() => {
                                        setSelectedTicket(ticket);
                                        setShowTicketDetails(true);
                                        fetchTicketDetails(ticket._id);
                                    }}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start space-x-4">
                                                <div className="flex-shrink-0">
                                                    <div className="p-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl group-hover:from-blue-200 group-hover:to-indigo-200 transition-colors">
                                                        <ChatBubbleLeftIcon className="h-6 w-6 text-blue-600" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center space-x-3 mb-2">
                                                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-900 transition-colors truncate">
                                                            {ticket.subject}
                                                        </h3>
                                                        <div className="flex items-center space-x-2">
                                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                                                                {getStatusIcon(ticket.status)}
                                                                <span className="ml-1">{ticket.status.replace('_', ' ').toUpperCase()}</span>
                                                            </span>
                                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                                                                {ticket.priority === 'urgent' && <ExclamationTriangleIcon className="h-3 w-3 mr-1" />}
                                                                {ticket.priority === 'high' && <ExclamationTriangleIcon className="h-3 w-3 mr-1" />}
                                                                {ticket.priority.toUpperCase()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                                        {ticket.description}
                                                    </p>
                                                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                                                        <span className="flex items-center">
                                                            <TagIcon className="h-4 w-4 mr-2 text-blue-500" />
                                                            <span className="font-medium">{ticket.category}</span>
                                                        </span>
                                                        <span className="flex items-center">
                                                            <CalendarIcon className="h-4 w-4 mr-2 text-green-500" />
                                                            <span className="font-medium">{formatDate(ticket.createdAt)}</span>
                                                        </span>
                                                        {ticket.assignedTo && (
                                                            <span className="flex items-center">
                                                                <UserIcon className="h-4 w-4 mr-2 text-purple-500" />
                                                                <span className="font-medium">{ticket.assignedTo.firstName} {ticket.assignedTo.lastName}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 ml-4">
                                            <div className="flex items-center space-x-2">
                                                <div className="p-2 bg-gray-100 group-hover:bg-blue-100 rounded-lg transition-colors">
                                                    <EyeIcon className="h-5 w-5 text-gray-600 group-hover:text-blue-600 transition-colors" />
                                                </div>
                                                {(user?.role === 'admin' || user?.role === 'crm') && (
                                                    <select
                                                        value={ticket.status}
                                                        onChange={(e) => handleStatusChange(ticket._id, e.target.value)}
                                                        className="text-xs border border-gray-300 rounded-lg px-3 py-2 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <option value="open">Open</option>
                                                        <option value="in_progress">In Progress</option>
                                                        <option value="resolved">Resolved</option>
                                                        <option value="closed">Closed</option>
                                                    </select>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                        <div className="px-8 py-6 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200 flex items-center justify-between">
                            <div className="text-sm text-gray-700 font-medium">
                                Showing page {pagination.current} of {pagination.pages}
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    onClick={() => setPagination({ ...pagination, current: pagination.current - 1 })}
                                    disabled={pagination.current === 1}
                                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all duration-200"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPagination({ ...pagination, current: pagination.current + 1 })}
                                    disabled={pagination.current === pagination.pages}
                                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all duration-200"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Ticket Modal */}
            {showCreateForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                    <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                        <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-white/20 rounded-lg">
                                        <PlusIcon className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold">Create Support Ticket</h3>
                                        <p className="text-blue-100 text-sm">Get help with your issues</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowCreateForm(false)}
                                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                >
                                    <XMarkIcon className="h-6 w-6 text-white" />
                                </button>
                            </div>
                        </div>
                        <div className="p-8">
                            <form onSubmit={handleCreateTicket} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Subject <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.subject}
                                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 transition-all duration-200"
                                            placeholder="Brief description of your issue"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                                        <select
                                            value={formData.category}
                                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 transition-all duration-200"
                                        >
                                            <option value="technical">Technical</option>
                                            <option value="billing">Billing</option>
                                            <option value="account">Account</option>
                                            <option value="task">Task</option>
                                            <option value="general">General</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
                                    <select
                                        value={formData.priority}
                                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 transition-all duration-200"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Description <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        required
                                        rows={5}
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 transition-all duration-200 resize-none"
                                        placeholder="Detailed description of your issue..."
                                    />
                                </div>

                                <div className="flex justify-end space-x-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowCreateForm(false)}
                                        className="px-6 py-3 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isCreatingTicket}
                                        className="px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200"
                                    >
                                        {isCreatingTicket ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <PlusIcon className="h-4 w-4 mr-2" />
                                                Create Ticket
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Ticket Details Modal */}
            {showTicketDetails && selectedTicket && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                    <div className="relative w-full max-w-6xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                        <div className="px-8 py-6 bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-3">
                                        <div className="p-2 bg-white/20 rounded-lg">
                                            <ChatBubbleLeftIcon className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold">{selectedTicket.subject}</h3>
                                            <div className="flex items-center space-x-4 mt-2">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedTicket.status)}`}>
                                                    {getStatusIcon(selectedTicket.status)}
                                                    <span className="ml-1">{selectedTicket.status.replace('_', ' ').toUpperCase()}</span>
                                                </span>
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(selectedTicket.priority)}`}>
                                                    {selectedTicket.priority === 'urgent' && <ExclamationTriangleIcon className="h-3 w-3 mr-1" />}
                                                    {selectedTicket.priority === 'high' && <ExclamationTriangleIcon className="h-3 w-3 mr-1" />}
                                                    {selectedTicket.priority.toUpperCase()}
                                                </span>
                                                <span className="text-sm text-gray-300">
                                                    {formatDate(selectedTicket.createdAt)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowTicketDetails(false)}
                                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                >
                                    <XMarkIcon className="h-6 w-6 text-white" />
                                </button>
                            </div>
                        </div>
                        <div className="p-8">

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Ticket Details */}
                                <div className="space-y-6">
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200">
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <ShieldCheckIcon className="h-6 w-6 text-blue-600" />
                                            </div>
                                            <h4 className="text-lg font-bold text-gray-900">Ticket Information</h4>
                                        </div>
                                        <div className="space-y-3 text-sm">
                                            <div className="flex items-center space-x-3">
                                                <UserIcon className="h-4 w-4 text-blue-500" />
                                                <span className="font-semibold text-gray-700">User:</span>
                                                <span className="text-gray-600">{selectedTicket.user.firstName} {selectedTicket.user.lastName}</span>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <TagIcon className="h-4 w-4 text-green-500" />
                                                <span className="font-semibold text-gray-700">Email:</span>
                                                <span className="text-gray-600">{selectedTicket.user.email}</span>
                                            </div>
                                            {selectedTicket.user.phone && (
                                                <div className="flex items-center space-x-3">
                                                    <CalendarIcon className="h-4 w-4 text-purple-500" />
                                                    <span className="font-semibold text-gray-700">Phone:</span>
                                                    <span className="text-gray-600">{selectedTicket.user.phone}</span>
                                                </div>
                                            )}
                                            <div className="flex items-center space-x-3">
                                                <TagIcon className="h-4 w-4 text-orange-500" />
                                                <span className="font-semibold text-gray-700">Category:</span>
                                                <span className="text-gray-600">{selectedTicket.category}</span>
                                            </div>
                                            {selectedTicket.assignedTo && (
                                                <div className="flex items-center space-x-3">
                                                    <UserIcon className="h-4 w-4 text-indigo-500" />
                                                    <span className="font-semibold text-gray-700">Assigned to:</span>
                                                    <span className="text-gray-600">{selectedTicket.assignedTo.firstName} {selectedTicket.assignedTo.lastName}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-6 rounded-2xl border border-gray-200">
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="p-2 bg-gray-100 rounded-lg">
                                                <DocumentTextIcon className="h-6 w-6 text-gray-600" />
                                            </div>
                                            <h5 className="text-lg font-bold text-gray-900">Description</h5>
                                        </div>
                                        <p className="text-sm text-gray-700 leading-relaxed">
                                            {selectedTicket.description}
                                        </p>
                                    </div>
                                </div>

                                {/* Messages */}
                                <div className="space-y-6">
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200">
                                        <div className="flex items-center space-x-3 mb-4">
                                            <div className="p-2 bg-green-100 rounded-lg">
                                                <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-600" />
                                            </div>
                                            <h4 className="text-lg font-bold text-gray-900">Conversation</h4>
                                        </div>
                                        <div className="space-y-4 max-h-80 overflow-y-auto">
                                            {selectedTicket.messages.map((message, index) => (
                                                <div key={index} className={`p-4 rounded-xl ${message.senderType === 'User'
                                                    ? 'bg-blue-100 border-l-4 border-blue-400'
                                                    : 'bg-green-100 border-l-4 border-green-400'
                                                    }`}>
                                                    <div className="flex justify-between items-start mb-2">
                                                        <div className="flex items-center space-x-2">
                                                            <div className={`p-1 rounded-full ${message.senderType === 'User' ? 'bg-blue-200' : 'bg-green-200'}`}>
                                                                <UserIcon className={`h-3 w-3 ${message.senderType === 'User' ? 'text-blue-600' : 'text-green-600'}`} />
                                                            </div>
                                                            <span className="text-xs font-semibold text-gray-700">
                                                                {message.sender.firstName} {message.sender.lastName}
                                                            </span>
                                                            {message.isInternal && (
                                                                <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                                                                    Internal
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-xs text-gray-500">
                                                            {formatDate(message.createdAt)}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-700 leading-relaxed">{message.message}</p>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Add Message */}
                                        <div className="mt-6 space-y-4">
                                            <div className="relative">
                                                <textarea
                                                    value={newMessage}
                                                    onChange={(e) => setNewMessage(e.target.value)}
                                                    placeholder="Type your message here..."
                                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white resize-none transition-all duration-200"
                                                    rows={3}
                                                />
                                            </div>
                                            {(user?.role === 'admin' || user?.role === 'crm') && (
                                                <label className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={isInternalMessage}
                                                        onChange={(e) => setIsInternalMessage(e.target.checked)}
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-600">Internal message (not visible to user)</span>
                                                </label>
                                            )}
                                            <button
                                                onClick={handleAddMessage}
                                                disabled={!newMessage.trim() || isAddingMessage}
                                                className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200"
                                            >
                                                {isAddingMessage ? (
                                                    <>
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                                                        Send Message
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerSupport;
